import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown } from 'lucide-react';
import CountryFlag from '../../../components/countryFlags/countryFlag';
import { cn } from '../../../lib/utils';

export const TRIGGER_CLASS = cn(
  'inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 shadow-sm',
  'cursor-pointer hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-300'
);

export const CONTENT_CLASS = cn(
  'z-50 min-w-[220px] rounded-xl border border-stone-200 bg-white p-1.5 shadow-lg',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=open]:fade-in data-[state=closed]:fade-out data-[side=bottom]:slide-in-from-top-1'
);

export const ITEM_CLASS = cn(
  'relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-2 text-sm text-stone-700 outline-none',
  'hover:bg-stone-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
);

export const ITEM_INDICATOR_CLASS =
  'absolute left-2 inline-flex h-4 w-4 items-center justify-center text-stone-900';

export function FilterTrigger({ label, selectedCount, activeLabel, activeAdornment = null }) {
  return (
    <>
      <span>{label}</span>
      {selectedCount > 0 ? (
        <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-stone-100 px-1.5 py-0.5 text-[11px] text-stone-600">
          {selectedCount}
        </span>
      ) : null}
      {activeLabel ? (
        <span className="hidden max-w-[120px] items-center gap-1.5 text-stone-500 md:inline-flex">
          {activeAdornment}
          <span className="truncate">{activeLabel}</span>
        </span>
      ) : null}
      <ChevronDown className="h-3.5 w-3.5 text-stone-500" />
    </>
  );
}

const MultiSelectFilter = ({
  label,
  selectedValues,
  options,
  onToggle,
  emptyLabel,
  showCountryFlags = false,
}) => {
  const activeOption = selectedValues.length === 1
    ? options.find((option) => option.value === selectedValues[0]) || null
    : null;
  const activeLabel = activeOption?.label || '';

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className={TRIGGER_CLASS}>
        <FilterTrigger
          label={label}
          selectedCount={selectedValues.length}
          activeLabel={activeLabel}
          activeAdornment={
            showCountryFlags && activeOption ? (
              <CountryFlag
                countryName={activeOption.value}
                className="h-3.5 w-5 flex-none rounded-sm"
              />
            ) : null
          }
        />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={cn(CONTENT_CLASS, 'max-h-[280px] overflow-y-auto')}
          side="bottom"
          sideOffset={6}
          align="start"
          collisionPadding={12}
        >
          {options.length ? (
            options.map((option) => {
              const checked = selectedValues.includes(option.value);

              return (
                <DropdownMenu.CheckboxItem
                  key={option.value}
                  checked={checked}
                  onCheckedChange={() => onToggle(option.value)}
                  onSelect={(event) => event.preventDefault()}
                  className={cn(ITEM_CLASS, 'pl-8')}
                >
                  <DropdownMenu.ItemIndicator className={ITEM_INDICATOR_CLASS}>
                    <Check className="h-4 w-4" />
                  </DropdownMenu.ItemIndicator>
                  {showCountryFlags ? (
                    <CountryFlag
                      countryName={option.value}
                      className="h-4 w-5 flex-none rounded-sm"
                    />
                  ) : null}
                  <span className="flex-1 truncate">{option.label}</span>
                  <span className="text-xs text-stone-400">{option.count}</span>
                </DropdownMenu.CheckboxItem>
              );
            })
          ) : (
            <div className="px-2 py-2 text-xs text-stone-500">{emptyLabel}</div>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default MultiSelectFilter;
